<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import {
  LIGHTING_PRESET_LIST,
  VIEWPORT_AMBIENT_INTENSITY_SCALE_MAX,
  VIEWPORT_AMBIENT_INTENSITY_SCALE_MIN,
  type LightingPresetId,
} from '@/constants/lightingPresets'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const { lightingPresetId, viewportAmbientIntensityScale } = storeToRefs(store)
const isExpanded = ref(true)
const hasUserToggled = ref(false)
const COLLAPSE_BREAKPOINT = 1480

const ambientLabel = computed(() => viewportAmbientIntensityScale.value.toFixed(2))

/**
 * @param id - 预设 id
 */
function selectPreset(id: LightingPresetId): void {
  store.setLightingPreset(id)
}

/**
 * @param e - range 的 input 事件
 */
function onAmbientInput(e: Event): void {
  const el = e.target as HTMLInputElement
  store.setViewportAmbientIntensityScale(Number(el.value))
}

/**
 * 根据窗口宽度设置灯光面板展开状态：宽屏展开、窄屏折叠。
 */
function syncDockExpandedByViewport(): void {
  if (hasUserToggled.value) return
  isExpanded.value = globalThis.innerWidth >= COLLAPSE_BREAKPOINT
}

/**
 * 切换灯光面板展开/折叠状态。
 */
function toggleDockExpanded(): void {
  hasUserToggled.value = true
  isExpanded.value = !isExpanded.value
}

onMounted(() => {
  syncDockExpandedByViewport()
  globalThis.addEventListener('resize', syncDockExpandedByViewport, { passive: true })
})

onUnmounted(() => {
  globalThis.removeEventListener('resize', syncDockExpandedByViewport)
})
</script>

<template>
  <div class="lightingDock" :aria-label="store.tUi('lightingDock.panelAria')">
    <button
      type="button"
      class="dockToggle"
      :aria-expanded="isExpanded ? 'true' : 'false'"
      aria-controls="lighting-dock-content"
      @click.stop="toggleDockExpanded"
    >
      <span>{{ store.tUi('lightingDock.title') }}</span>
      <span class="dockToggleIcon" :class="{ expanded: isExpanded }" aria-hidden="true">▾</span>
    </button>
    <div v-show="isExpanded" id="lighting-dock-content" class="dockBody">
      <div class="presetRow" role="toolbar" :aria-label="store.tUi('lightingDock.presetsAria')">
      <button
        v-for="p in LIGHTING_PRESET_LIST"
        :key="p.id"
        type="button"
        class="sphereBtn"
        :class="{ on: lightingPresetId === p.id }"
        :title="p.label"
        :aria-label="store.tUi('lightingDock.presetAria', { label: p.label })"
        :aria-pressed="lightingPresetId === p.id"
        @click.stop="selectPreset(p.id)"
      >
        <span class="sphere" :style="{ background: p.spherePreviewCss }" aria-hidden="true" />
      </button>
    </div>
      <div class="ambientRow">
        <label class="ambientLab" for="im-ambient-scale">{{ store.tUi('lightingDock.ambientStrength') }}</label>
        <input
          id="im-ambient-scale"
          class="ambientRange"
          type="range"
          :min="VIEWPORT_AMBIENT_INTENSITY_SCALE_MIN"
          :max="VIEWPORT_AMBIENT_INTENSITY_SCALE_MAX"
          :step="0.05"
          :value="viewportAmbientIntensityScale"
          :aria-valuetext="store.tUi('lightingDock.ambientValue', { value: ambientLabel })"
          @input="onAmbientInput"
        />
        <span class="ambientVal" aria-hidden="true">{{ ambientLabel }}×</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.lightingDock {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.45rem;
  padding: 0.4rem 0.5rem 0.45rem;
  min-width: 200px;
  border-radius: 14px;
  border: 1px solid var(--im-border, #e0e0e0);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: var(--im-shadow, 0 4px 18px rgba(0, 0, 0, 0.06));
  pointer-events: auto;
  backdrop-filter: blur(6px);
}

.dockToggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.7);
  color: #111;
  padding: 0.3rem 0.35rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
}

.dockToggleIcon {
  transition: transform 140ms ease;
}

.dockToggleIcon.expanded {
  transform: rotate(180deg);
}

.dockBody {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.presetRow {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.ambientRow {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.35rem 0.45rem;
  padding-top: 0.1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.ambientLab {
  font-size: 0.72rem;
  font-weight: 650;
  color: #333;
  white-space: nowrap;
}

.ambientRange {
  width: 100%;
  min-width: 0;
  accent-color: #111;
}

.ambientVal {
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: var(--im-muted, #666);
  min-width: 2.5rem;
  text-align: right;
}

.sphereBtn {
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-sizing: border-box;
}

.sphereBtn:focus-visible {
  outline: 2px solid #111;
  outline-offset: 2px;
}

.sphereBtn.on {
  outline: 2px solid #111;
  outline-offset: 0;
}

.sphere {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  box-shadow:
    inset -3px -4px 8px rgba(0, 0, 0, 0.22),
    inset 2px 3px 6px rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.12);
}

@media (max-width: 1480px) {
  .lightingDock {
    min-width: 168px;
    max-width: min(72vw, 230px);
  }
}
</style>
